import React, { useEffect, useRef, FC, useMemo } from 'react'
import classNames from 'classnames'
import SALink from 'components/SALink'
import { getLogout } from 'apis/authentication'
import { lockScroll, unlockScroll } from 'lib/lockScroll'
import OpenNew from 'svg/SA/open-new-os.svg'
import NavMenu from '../NavMenu/NavMenu'
import { useNavigation } from '../NavigationContext'
import { NavItem, isActiveNavItem } from '../helpers/constants'
import { buildStudioNavigationLinks, handleStudioLinkClick } from '../helpers/navigate'
import styles from './Sidebar.module.scss'
import useQA from 'hooks/useQA'
import { useSelector } from 'react-redux'
import { getIsMobileTraffic } from 'selectors/shared/mobileTraffic'
import { getArtistProfileUrl } from '../helpers/selectors'
import { getIsArtistOnboarded } from 'selectors/studio'

type SidebarProps = { fullName: string }

const Sidebar: FC<SidebarProps> = ({ fullName }) => {
  const {
    isMobileNavOpen: isMobileOpen,
    isDesktopCollapsed,
    openSectionTitle,
    onToggleSection,
    onCloseMobileNav: onNavigate,
    activeHref,
  } = useNavigation()
  const { testId } = useQA()
  const artistProfileUrl = useSelector(getArtistProfileUrl)
  const isMobileTraffic = useSelector(getIsMobileTraffic)
  const isArtistOnboarded = useSelector(getIsArtistOnboarded)

  const sidebarRef = useRef<HTMLElement>(null)
  const navSections = useMemo(() => buildStudioNavigationLinks({ isArtistOnboarded }), [
    isArtistOnboarded,
  ])

  useEffect(() => {
    isMobileOpen ? lockScroll() : unlockScroll()
  }, [isMobileOpen])

  const handleNavItemClick = async (event: React.SyntheticEvent<HTMLElement>, item: NavItem) => {
    if (item.isLogout) {
      event.preventDefault()
      await getLogout()
      location.reload()
    }
  }

  return (
    <aside
      ref={sidebarRef}
      className={classNames(styles.sidebar, {
        [styles.sidebarDesktopCollapsed]: isDesktopCollapsed,
        [styles.sidebarMobileOpen]: isMobileOpen,
      })}
    >
      <nav className={styles.sidebarNav}>
        {isMobileTraffic && (
          <SALink href={artistProfileUrl} className={styles.userName}>
            {fullName}
          </SALink>
        )}
        <div className={styles.sections} {...testId('side-bar-sections')}>
          {navSections.map((section) => {
            const isSingleItemSection = section.items.length === 1

            if (section.isSingleClickableSection && isSingleItemSection) {
              const item = section.items[0]
              const isActive = Boolean(activeHref && isActiveNavItem(activeHref, item))

              return (
                <SALink
                  key={section.title}
                  href={item.href}
                  className={classNames(styles.standaloneLink, {
                    [styles.standaloneLinkActive]: isActive,
                  })}
                  target={item.openInNewTab ? '_blank' : ''}
                  rel={item.openInNewTab ? 'noopener noreferrer' : ''}
                  onClick={(event: React.MouseEvent<HTMLAnchorElement>) => {
                    handleStudioLinkClick(event, item)
                    onNavigate()
                  }}
                >
                  <span>{item.label}</span>
                  {item.showIcon && <OpenNew className={styles.icon} />}
                </SALink>
              )
            }

            return (
              <NavMenu
                key={section.title}
                title={section.title}
                items={section.items}
                isOpen={openSectionTitle === section.title}
                onToggle={() => onToggleSection(section.title)}
                onNavigate={onNavigate}
                onItemClick={handleNavItemClick}
                activeHref={activeHref}
              />
            )
          })}
        </div>
      </nav>
    </aside>
  )
}

export default Sidebar
